import React, {createContext, useContext, useState, FC, ReactNode} from 'react';
import * as actions from "../actions/priceCatalogActions";
import {useDispatch} from "react-redux";
import {FormikProvider, useFormik} from "formik";
import * as _ from "lodash";
import {messages as M} from '../../../../_metronic/i18n/messages';
import {PriceCatalogItem} from "../types";
import {showGlobalMessage} from "../../_common/components/GlobalMessage/actions/showGlobalMessage";
import {AppDispatch} from "../../../../redux/store";

interface AddEditItemContextValue {
  id: number
  isEdit: boolean
  handleSubmit: () => Promise<void>
  formik: ReturnType<typeof useFormik<FormValues>>;
  getItemDetails: () => Promise<void>
}

interface PriceCatalogProviderProps {
  id: number;
  children: ReactNode;
}

interface FormValues {
  mainCategory: string;
  secondCategory: string;
  serviceDescription: string;
  servicePrice: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>;

const AddEditItemContext = createContext<AddEditItemContextValue | null>(null);

export function useAddEditItemContext(): AddEditItemContextValue {
  const context = useContext(AddEditItemContext);
  if (!context) {
    throw new Error('useAddEditItemContext must be used within a PriceCatalogProvider');
  }
  return context;
}

const defaultPriceCatalogItem = {
  id: -1,
  firmID: 0,
  mainCategory: '',
  secondCategory: '',
  serviceDescription: '',
  servicePrice: '',
  notes: ''
}

export const AddEditItemProvider: FC<PriceCatalogProviderProps> = ({children, id}) => {
  const isEdit = id !== -1;
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<PriceCatalogItem>(defaultPriceCatalogItem);

  const getItemDetails = async () => {
    const result = await actions.getItemDetails(id);
    if (result) setData(result)
  };

  const validate = (values: FormValues): FormErrors => {
    const errors: FormErrors = {};
    const requiredFields: (keyof FormValues)[] = [
      'mainCategory',
      'secondCategory',
      'serviceDescription',
      'servicePrice',
    ];

    requiredFields.forEach((field) => {
      if (!values[field]) errors[field] = 'Required field';
    });

    return errors;
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      mainCategory: _.get(data, 'mainCategory', ''),
      secondCategory: _.get(data, 'secondCategory', ''),
      serviceDescription: _.get(data, 'serviceDescription', ''),
      servicePrice: _.get(data, 'servicePrice', ''),
      notes: _.get(data, 'notes', ''),
    },
    enableReinitialize: true,
    validate,
    validateOnMount: true,
    onSubmit: () => {
    },
  });

  const handleSubmit = async (): Promise<void> => {
    const data = {
      id: isEdit ? id : -1,
      mainCategory: formik.values.mainCategory,
      secondCategory: formik.values.secondCategory,
      serviceDescription: formik.values.serviceDescription,
      servicePrice: formik.values.servicePrice,
      notes: formik.values.notes,
    }
    const result = await dispatch(actions.addEditItem(data));

    if (result === 0) {
      formik.resetForm();
    } else {
      dispatch(showGlobalMessage('error', M['-1']));
    }
  };

  const value: AddEditItemContextValue = {
    id,
    isEdit,
    handleSubmit,
    formik,
    getItemDetails
  };

  return (
    <AddEditItemContext.Provider value={value}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </AddEditItemContext.Provider>
  );
};